
import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { API_BASE } from "../../../ApiUrl";
import "./TenantWorkFlow.css";
import WorkflowTabs from "./WorflowTabs";
import TenantWorkflowTable from "./TenantWorkFlowTable";

const WORKFLOWS_API_URL = `${API_BASE}/tenant-workflows/combined/`;
const WORKFLOW_DELETE_URL = `${API_BASE}/tenant-workflows/combined/delete/`;
const ROLES_API_URL = `${API_BASE}/roles/`;

const WORKFLOW_TYPE_OPTIONS = [
  { value: "TENANT_VERIFICATION", label: "Tenant Verification" },
  { value: "TEMPLATE_APPROVAL", label: "Template Approval" },
  { value: "DOCUMENT_APPROVAL", label: "Document Approval" },
  { value: "PROJECT_APPROVAL", label: "Project Approval" },
  { value: "GENERAL_APPROVAL", label: "General Approval" }
];

const WORKFLOW_STATUS_OPTIONS = [
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "COMPLETED", label: "Completed" },
  { value: "REJECTED", label: "Rejected" },
  { value: "CANCELLED", label: "Cancelled" }
];

const REFERENCE_TABLE_OPTIONS = [
  { value: "TENANT", label: "Tenant" },
  { value: "TENANT_DOCUMENT", label: "Tenant Document" },
  { value: "PROJECT", label: "Project" },
  { value: "TEMPLATE", label: "Template" }
];

const DECISION_OPTIONS = [
  { value: "", label: "Pending" },
  { value: "APPROVED", label: "Approved" },
  { value: "REJECTED", label: "Rejected" },
  { value: "RETURNED", label: "Returned" }
];

const emptyStep = {
  id: null,
  step_order: 1,
  assigned_role: "",
  decision: "",
  comments: "",
  actioned_by: null,
  actioned_at: null
};

const emptyWorkflow = {
  id: null,
  workflow_type: "",
  reference_table: "",
  reference_id: "",
  status: "IN_PROGRESS",
  steps: [{ ...emptyStep }]
};

const TenantWorkflow = () => {
  const [workflows, setWorkflows] = useState([]);
  const [rolesList, setRolesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deletedStepIds, setDeletedStepIds] = useState([]);

  const getTenantUser = () => {
    try {
      const tenantUser = localStorage.getItem("tenant_user");
      return tenantUser ? JSON.parse(tenantUser) : null;
    } catch (error) {
      console.error("Error reading tenant_user:", error);
      return null;
    }
  };

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await axios.get(ROLES_API_URL);

        const data = response.data?.data
          ? response.data.data
          : Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];

        setRolesList(data);
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };

    fetchRoles();
  }, []);

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    const tenantUser = getTenantUser();

    if (!tenantUser?.tenant_id) {
      setWorkflows([]);
      setLoadingExisting(false);

      Swal.fire({
        icon: "error",
        title: "Tenant Information Missing",
        text: "Please login again."
      });

      return;
    }

    try {
      setLoadingExisting(true);

      const response = await axios.get(WORKFLOWS_API_URL);

      console.log("Combined workflows response:", response.data);

      let data = response.data?.data || response.data;

      if (!Array.isArray(data)) {
        data = data?.results || [];
      }

      data = data.filter(
        (workflow) =>
          Number(workflow.tenant) === Number(tenantUser.tenant_id)
      );

      const mappedWorkflows = data.map((workflow) => {
        const steps = Array.isArray(workflow.steps)
          ? workflow.steps.map((step) => ({
              id: step.id || null,
              step_order: step.step_order ?? 1,
              assigned_role: step.assigned_role
                ? String(step.assigned_role)
                : "",
              decision: step.decision || "",
              comments: step.comments || "",
              actioned_by: step.actioned_by || null,
              actioned_at: step.actioned_at || null
            }))
          : [];

        steps.sort(
          (a, b) =>
            Number(a.step_order) - Number(b.step_order)
        );

        return {
          id: workflow.id || null,
          workflow_type: workflow.workflow_type || "",
          reference_table: workflow.reference_table || "",
          reference_id: workflow.reference_id || "",
          status: workflow.status || "IN_PROGRESS",
          tenant: workflow.tenant,
          steps: steps.length
            ? steps
            : [{ ...emptyStep }]
        };
      });

      setWorkflows(mappedWorkflows);
    } catch (error) {
      console.error(
        "Error fetching workflows:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to Load Workflows",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Could not fetch existing workflows."
      });

      setWorkflows([]);
    } finally {
      setLoadingExisting(false);
    }
  };

  const handleAdd = () => {
    setWorkflows([
      {
        ...emptyWorkflow,
        steps: [{ ...emptyStep }]
      }
    ]);

    setDeletedStepIds([]);
    setEditingId(null);
    setIsEditing(false);
    setShowForm(true);
  };

  const handleAddAnother = () => {
    setWorkflows((prev) => [
      ...prev,
      {
        ...emptyWorkflow,
        steps: [{ ...emptyStep }]
      }
    ]);
  };

  const handleWorkflowChange = (workflowIndex, e) => {
    const { name, value } = e.target;

    setWorkflows((prev) =>
      prev.map((workflow, index) =>
        index === workflowIndex
          ? {
              ...workflow,
              [name]: value
            }
          : workflow
      )
    );
  };

  const handleStepChange = (
    workflowIndex,
    stepIndex,
    e
  ) => {
    const { name, value } = e.target;

    setWorkflows((prev) =>
      prev.map((workflow, index) => {
        if (index !== workflowIndex) {
          return workflow;
        }

        return {
          ...workflow,
          steps: workflow.steps.map(
            (step, index) =>
              index === stepIndex
                ? {
                    ...step,
                    [name]: value
                  }
                : step
          )
        };
      })
    );
  };

  const addStep = (workflowIndex) => {
    setWorkflows((prev) =>
      prev.map((workflow, index) => {
        if (index !== workflowIndex) {
          return workflow;
        }

        const nextOrder =
          workflow.steps.length + 1;

        return {
          ...workflow,
          steps: [
            ...workflow.steps,
            {
              ...emptyStep,
              step_order: nextOrder
            }
          ]
        };
      })
    );
  };

  const removeStep = (
    workflowIndex,
    stepIndex
  ) => {
    setWorkflows((prev) =>
      prev.map((workflow, index) => {
        if (index !== workflowIndex) {
          return workflow;
        }

        const step =
          workflow.steps[stepIndex];

        if (isEditing && step?.id) {
          setDeletedStepIds((previousIds) =>
            previousIds.includes(step.id)
              ? previousIds
              : [...previousIds, step.id]
          );
        }

        const remainingSteps =
          workflow.steps.filter(
            (_, index) => index !== stepIndex
          );

        return {
          ...workflow,
          steps: remainingSteps.length
            ? remainingSteps
            : [{ ...emptyStep }]
        };
      })
    );
  };

  const removeWorkflow = (workflowIndex) => {
    setWorkflows((prev) =>
      prev.filter(
        (_, index) => index !== workflowIndex
      )
    );
  };

  const handleEdit = async (workflow) => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${WORKFLOWS_API_URL}${workflow.id}/`
      );

      console.log(
        "Single workflow response:",
        response.data
      );

      const data =
        response.data?.data ||
        response.data;

      const steps =
        Array.isArray(data.steps) &&
        data.steps.length > 0
          ? data.steps.map((step) => ({
              id: step.id || null,
              step_order: step.step_order ?? 1,
              assigned_role: step.assigned_role
                ? String(step.assigned_role)
                : "",
              decision: step.decision || "",
              comments: step.comments || "",
              actioned_by:
                step.actioned_by || null,
              actioned_at:
                step.actioned_at || null
            }))
          : [{ ...emptyStep }];

      steps.sort(
        (a, b) =>
          Number(a.step_order) -
          Number(b.step_order)
      );

      setWorkflows([
        {
          id: data.id || workflow.id,
          workflow_type:
            data.workflow_type || "",
          reference_table:
            data.reference_table || "",
          reference_id:
            data.reference_id || "",
          status:
            data.status || "IN_PROGRESS",
          tenant: data.tenant,
          steps
        }
      ]);

      setDeletedStepIds([]);
      setEditingId(data.id || workflow.id);
      setIsEditing(true);
      setShowForm(true);
    } catch (error) {
      console.error(
        "Error fetching workflow:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Unable to Load Workflow",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to load workflow details."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (workflow) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Delete Workflow?",
      html: `
        <p>
          Are you sure you want to delete
          <strong>
            ${workflow.workflow_type || "this workflow"}
          </strong>?
        </p>
        <p style="color:#dc2626;font-size:13px;">
          This will also delete all steps associated
          with this workflow.
        </p>
      `,
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc2626"
    });

    if (!result.isConfirmed) {
      return;
    }

    const tenantUser = getTenantUser();

    try {
      setLoading(true);

      await axios.delete(
        WORKFLOW_DELETE_URL,
        {
          data: {
            id: workflow.id,
            tenant: Number(
              tenantUser?.tenant_id
            )
          }
        }
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Workflow and its steps were deleted successfully.",
        timer: 1600,
        showConfirmButton: false
      });

      await fetchWorkflows();
    } catch (error) {
      console.error(
        "Error deleting workflow:",
        error
      );

      Swal.fire({
        icon: "error",
        title: "Delete Failed",
        text:
          error.response?.data?.detail ||
          error.response?.data?.message ||
          "Unable to delete the workflow."
      });
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    for (
      let workflowIndex = 0;
      workflowIndex < workflows.length;
      workflowIndex++
    ) {
      const workflow =
        workflows[workflowIndex];

      if (!workflow.workflow_type) {
        Swal.fire({
          icon: "warning",
          title: "Workflow Type Required",
          text: `Please select Workflow Type for Workflow ${
            workflowIndex + 1
          }.`
        });

        return false;
      }

      if (!workflow.reference_table) {
        Swal.fire({
          icon: "warning",
          title: "Reference Table Required",
          text: `Please select Reference Table for Workflow ${
            workflowIndex + 1
          }.`
        });

        return false;
      }

      if (!workflow.reference_id?.trim()) {
        Swal.fire({
          icon: "warning",
          title: "Reference ID Required",
          text: `Please enter Reference ID for Workflow ${
            workflowIndex + 1
          }.`
        });

        return false;
      }

      for (
        let stepIndex = 0;
        stepIndex < workflow.steps.length;
        stepIndex++
      ) {
        const step =
          workflow.steps[stepIndex];

        const hasStepValue =
          step.assigned_role ||
          step.comments?.trim() ||
          step.id;

        if (!hasStepValue) {
          continue;
        }

        if (
          !step.step_order ||
          Number(step.step_order) < 1
        ) {
          Swal.fire({
            icon: "warning",
            title: "Invalid Step Order",
            text: `Workflow ${
              workflowIndex + 1
            }, Step ${
              stepIndex + 1
            }: Step Order must be 1 or greater.`
          });

          return false;
        }

        if (!step.assigned_role) {
          Swal.fire({
            icon: "warning",
            title: "Assigned Role Required",
            text: `Please select Assigned Role for Workflow ${
              workflowIndex + 1
            }, Step ${stepIndex + 1}.`
          });

          return false;
        }
      }
    }

    return true;
  };

  const buildNewSteps = (workflow) => {
    return workflow.steps
      .filter(
        (step) =>
          !step.id &&
          (step.assigned_role ||
            step.comments?.trim())
      )
      .map((step) => ({
        step_order:
          Number(step.step_order) || 1,
        assigned_role:
          step.assigned_role
            ? Number(step.assigned_role)
            : null,
        decision: step.decision || "",
        comments:
          (step.comments || "").trim(),
        actioned_by:
          step.actioned_by || null,
        actioned_at:
          step.actioned_at || null
      }));
  };

  const buildUpdatedSteps = (workflow) => {
    return workflow.steps
      .filter((step) => step.id)
      .map((step) => ({
        id: step.id,
        step_order:
          Number(step.step_order) || 1,
        assigned_role:
          step.assigned_role
            ? Number(step.assigned_role)
            : null,
        decision: step.decision || "",
        comments:
          (step.comments || "").trim(),
        actioned_by:
          step.actioned_by || null,
        actioned_at:
          step.actioned_at || null
      }));
  };

  const buildCreatePayload = (
    workflow,
    tenantUser
  ) => ({
    tenant: Number(tenantUser.tenant_id),
    workflow_type:
      workflow.workflow_type,
    reference_table:
      workflow.reference_table,
    reference_id:
      workflow.reference_id.trim(),
    status: workflow.status,
    steps: workflow.steps
      .filter(
        (step) =>
          step.assigned_role ||
          step.comments?.trim()
      )
      .map((step) => ({
        step_order:
          Number(step.step_order) || 1,
        assigned_role:
          step.assigned_role
            ? Number(step.assigned_role)
            : null,
        decision: step.decision || "",
        comments:
          (step.comments || "").trim(),
        actioned_by:
          step.actioned_by || null,
        actioned_at:
          step.actioned_at || null
      }))
  });

  const buildUpdatePayload = (workflow) => ({
    workflow_type:
      workflow.workflow_type,
    reference_table:
      workflow.reference_table,
    reference_id:
      workflow.reference_id.trim(),
    status: workflow.status,
    steps: {
      updated:
        buildUpdatedSteps(workflow),
      deleted_ids: deletedStepIds,
      new:
        buildNewSteps(workflow)
    }
  });

  const extractError = (error) => {
    if (error.response?.data) {
      const data = error.response.data;

      if (typeof data === "string") {
        return data;
      }

      if (data.detail) {
        return data.detail;
      }

      if (data.message) {
        return data.message;
      }

      if (data.errors) {
        return Object.entries(data.errors)
          .map(
            ([field, errors]) =>
              `${field}: ${
                Array.isArray(errors)
                  ? errors.join(", ")
                  : errors
              }`
          )
          .join(" | ");
      }

      return JSON.stringify(data);
    }

    return (
      error.message ||
      "Unknown error"
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const tenantUser = getTenantUser();

    if (!tenantUser?.tenant_id) {
      Swal.fire({
        icon: "error",
        title: "Tenant Information Missing",
        text: "Please login again."
      });

      return;
    }

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);

      if (isEditing) {
        const workflow = workflows[0];

        const payload =
          buildUpdatePayload(workflow);

        console.log(
          "Update workflow payload:",
          payload
        );

        await axios.put(
          `${WORKFLOWS_API_URL}${editingId}/`,
          payload,
          {
            headers: {
              "Content-Type":
                "application/json"
            }
          }
        );

        await Swal.fire({
          icon: "success",
          title: "Workflow Updated",
          text: "Workflow and steps updated successfully.",
          timer: 1600,
          showConfirmButton: false
        });
      } else {
        for (const workflow of workflows) {
          const payload =
            buildCreatePayload(
              workflow,
              tenantUser
            );

          console.log(
            "Create workflow payload:",
            payload
          );

          await axios.post(
            WORKFLOWS_API_URL,
            payload,
            {
              headers: {
                "Content-Type":
                  "application/json"
              }
            }
          );
        }

        await Swal.fire({
          icon: "success",
          title: "Workflows Created",
          text: `${workflows.length} workflow${
            workflows.length === 1
              ? ""
              : "s"
          } created successfully.`,
          timer: 1600,
          showConfirmButton: false
        });
      }

      handleCancel();
      await fetchWorkflows();
    } catch (error) {
      console.error(
        "Error saving workflow:",
        error
      );

      Swal.fire({
        icon: "error",
        title: isEditing
          ? "Update Failed"
          : "Creation Failed",
        text: extractError(error)
      });
    } finally {
      setLoading(false);
    }
  };

const handleCancel = async () => {
  setShowForm(false);
  setIsEditing(false);
  setEditingId(null);
  setDeletedStepIds([]);
  await fetchWorkflows();
};

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="tenant-workflow-content">
          <WorkflowTabs />

          {!showForm ? (
            <>
              <div className="tenant-workflow-page-header">
                <div>
                  <h1>Tenant Workflows</h1>
                </div>

                <button
                  type="button"
                  className="workflow-add-btn"
                  onClick={handleAdd}
                >
                  + Add Workflow
                </button>
              </div>

              <div className="workflow-list-card">
                <TenantWorkflowTable
                  workflows={workflows}
                  loading={loadingExisting || loading}
                  rolesList={rolesList}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              </div>
            </>
          ) : (
            <>
              <div className="tenant-workflow-page-header">
                <div>
                  <h1>
                    {isEditing
                      ? "Edit Tenant Workflow"
                      : "Create Tenant Workflows"}
                  </h1>
                </div>

                <button
                  type="button"
                  className="workflow-cancel-btn"
                  onClick={handleCancel}
                >
                  ← Back
                </button>
              </div>

              {loadingExisting ? (
                <div className="workflow-loading">
                  Loading...
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  {workflows.map(
                    (workflow, workflowIndex) => (
                      <div
                        className="workflow-form-card"
                        key={
                          workflow.id ||
                          `new-workflow-${workflowIndex}`
                        }
                        style={{
                          marginBottom: 20
                        }}
                      >
                        <div className="workflow-form-header">
                          <h2>
                            {isEditing
                              ? "Workflow"
                              : `Workflow ${
                                  workflowIndex + 1
                                }`}
                          </h2>

                          {!isEditing && (
                            <button
                              type="button"
                              className="workflow-remove-btn"
                              onClick={() =>
                                removeWorkflow(
                                  workflowIndex
                                )
                              }
                            >
                              Remove
                            </button>
                          )}
                        </div>

                        <div className="workflow-form-body">
                          <div className="workflow-form-grid">
                            <div className="workflow-field">
                              <label>
                                Workflow Type
                              </label>

                              <select
                                name="workflow_type"
                                value={
                                  workflow.workflow_type
                                }
                                onChange={(e) =>
                                  handleWorkflowChange(
                                    workflowIndex,
                                    e
                                  )
                                }
                              >
                                <option value="">
                                  Select
                                </option>

                                {WORKFLOW_TYPE_OPTIONS.map(
                                  (option) => (
                                    <option
                                      key={
                                        option.value
                                      }
                                      value={
                                        option.value
                                      }
                                    >
                                      {option.label}
                                    </option>
                                  )
                                )}
                              </select>
                            </div>

                            <div className="workflow-field">
                              <label>
                                Status
                              </label>

                              <select
                                name="status"
                                value={
                                  workflow.status
                                }
                                onChange={(e) =>
                                  handleWorkflowChange(
                                    workflowIndex,
                                    e
                                  )
                                }
                              >
                                {WORKFLOW_STATUS_OPTIONS.map(
                                  (option) => (
                                    <option
                                      key={
                                        option.value
                                      }
                                      value={
                                        option.value
                                      }
                                    >
                                      {option.label}
                                    </option>
                                  )
                                )}
                              </select>
                            </div>

                            <div className="workflow-field">
                              <label>
                                Reference Table
                              </label>

                              <select
                                name="reference_table"
                                value={
                                  workflow.reference_table
                                }
                                onChange={(e) =>
                                  handleWorkflowChange(
                                    workflowIndex,
                                    e
                                  )
                                }
                              >
                                <option value="">
                                  Select
                                </option>

                                {REFERENCE_TABLE_OPTIONS.map(
                                  (option) => (
                                    <option
                                      key={
                                        option.value
                                      }
                                      value={
                                        option.value
                                      }
                                    >
                                      {option.label}
                                    </option>
                                  )
                                )}
                              </select>
                            </div>

                            <div className="workflow-field">
                              <label>
                                Reference ID
                              </label>

                              <input
                                type="text"
                                name="reference_id"
                                value={
                                  workflow.reference_id
                                }
                                onChange={(e) =>
                                  handleWorkflowChange(
                                    workflowIndex,
                                    e
                                  )
                                }
                                placeholder="UUID"
                              />
                            </div>
                          </div>

                          <div className="workflow-steps-subsection">
                            <div className="workflow-steps-subsection-header">
                              <h5>
                                Steps
                              </h5>

                              <button
                                type="button"
                                className="workflow-steps-add-btn"
                                onClick={() =>
                                  addStep(
                                    workflowIndex
                                  )
                                }
                              >
                                <span>+</span>
                                Add
                              </button>
                            </div>

                            {workflow.steps.map(
                              (
                                step,
                                stepIndex
                              ) => (
                                <div
                                  className="workflow-step-row"
                                  key={
                                    step.id ||
                                    `new-step-${workflowIndex}-${stepIndex}`
                                  }
                                >
                                  <div className="workflow-step-fields">
                                    <div className="workflow-field">
                                      <label>
                                        Step Order
                                      </label>

                                      <input
                                        type="number"
                                        name="step_order"
                                        value={
                                          step.step_order
                                        }
                                        onChange={(
                                          e
                                        ) =>
                                          handleStepChange(
                                            workflowIndex,
                                            stepIndex,
                                            e
                                          )
                                        }
                                        min="1"
                                      />
                                    </div>

                                    <div className="workflow-field">
                                      <label>
                                        Assigned Role
                                      </label>

                                      <select
                                        name="assigned_role"
                                        value={
                                          step.assigned_role
                                        }
                                        onChange={(
                                          e
                                        ) =>
                                          handleStepChange(
                                            workflowIndex,
                                            stepIndex,
                                            e
                                          )
                                        }
                                      >
                                        <option value="">
                                          Select
                                        </option>

                                        {rolesList.map(
                                          (role) => (
                                            <option
                                              key={
                                                role.id
                                              }
                                              value={
                                                role.id
                                              }
                                            >
                                              {role.name ||
                                                role.role_name ||
                                                role.code ||
                                                `Role ${role.id}`}
                                            </option>
                                          )
                                        )}
                                      </select>
                                    </div>

                                    <div className="workflow-field">
                                      <label>
                                        Decision
                                      </label>

                                      <select
                                        name="decision"
                                        value={
                                          step.decision
                                        }
                                        onChange={(
                                          e
                                        ) =>
                                          handleStepChange(
                                            workflowIndex,
                                            stepIndex,
                                            e
                                          )
                                        }
                                      >
                                        {DECISION_OPTIONS.map(
                                          (
                                            option
                                          ) => (
                                            <option
                                              key={
                                                option.value
                                              }
                                              value={
                                                option.value
                                              }
                                            >
                                              {
                                                option.label
                                              }
                                            </option>
                                          )
                                        )}
                                      </select>
                                    </div>
                                  </div>

                                  <div className="workflow-field workflow-step-comments">
                                    <label>
                                      Comments
                                    </label>

                                    <textarea
                                      name="comments"
                                      value={
                                        step.comments
                                      }
                                      onChange={(
                                        e
                                      ) =>
                                        handleStepChange(
                                          workflowIndex,
                                          stepIndex,
                                          e
                                        )
                                      }
                                      rows={2}
                                      placeholder="Comments"
                                    />
                                  </div>

                                  <div className="workflow-step-actions">
                                    <button
                                      type="button"
                                      className="workflow-step-remove-btn"
                                      onClick={() =>
                                        removeStep(
                                          workflowIndex,
                                          stepIndex
                                        )
                                      }
                                    >
                                      Remove
                                    </button>
                                  </div>
                                </div>
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}

                  {!isEditing && (
                    <button
                      type="button"
                      className="workflow-add-workflow-btn"
                      onClick={handleAddAnother}
                      style={{
                        marginBottom: 20
                      }}
                    >
                      <span>+</span>
                      Add Another Workflow
                    </button>
                  )}

                  <div className="workflow-form-actions workflow-form-actions--standalone">
                    <button
                      type="button"
                      className="workflow-cancel-btn"
                      onClick={handleCancel}
                      disabled={loading}
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="workflow-save-btn"
                      disabled={loading}
                    >
                      {loading
                        ? "Saving..."
                        : isEditing
                        ? "Update Workflow"
                        : "Save Workflows"}
                    </button>
                  </div>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantWorkflow;

